/**
 * SEE-974 — Vista equipment coding dropdowns (Equipment, Equipment Cost Code,
 * Equipment Cost Type).
 *
 * These mirror the other Agave dropdown wrappers (AgaveCostTypesDropdown etc.):
 * a thin component per coding field that emits the selected entity, so
 * renderAgaveDropdown can treat equipment exactly like every other coding column.
 *
 * SEE-1435: Equipment now searches server-side through CodingEntityDropdown /
 * useAgaveCodingDropdown, like every other coding entity. It previously shared
 * useEquipmentCodingOptions' client-side fetch-all, which capped the list at 500
 * rows and so hid most of a large fleet (Griffith has 3,679 records).
 *
 * SEE-1325: the two EM dropdowns followed. They were the last coding entity on a
 * client-side fetch, kept there by SEE-1435 because their EMGroup / department /
 * cost-code-pair cascade filtered in memory. That cascade now runs in
 * `search_agave_em_cost_codes` / `search_agave_em_cost_types`, with the pair
 * backed by the AgaveEmCostCodeCostType join, so equipment coding matches the Job
 * Cost shape end to end.
 *
 * All three still scope their OPTIONS to the selected equipment's EMGroup (empty
 * until an equipment is picked) but stay editable the whole time — picking
 * equipment first is the obvious flow, so they shouldn't render greyed-out.
 */
import { AgaveEquipment, AgaveEmCostCode, AgaveEmCostType } from 'API';
import CodingEntityDropdown from 'app/pages/overview/tabs/transactions/CodingEntityDropdown';

interface EquipmentDropdownProps {
  selectedEquipment?: AgaveEquipment | null;
  onValueChange: (option: AgaveEquipment | null) => void;
  disabled?: boolean;
  enabled?: boolean;
  placeholder?: string | null;
}

// SEE-1435: server-side search via the shared coding dropdown, which also
// supplies the label format (name-first off, code-first on — SEE-1325) and the
// row-click guard.
export const AgaveEquipmentDropdown = ({
  selectedEquipment,
  onValueChange,
  disabled,
  enabled = true,
  placeholder,
}: EquipmentDropdownProps): JSX.Element => {
  return (
    <CodingEntityDropdown<AgaveEquipment>
      entityType="equipment"
      label="Equipment"
      selectedItem={selectedEquipment}
      onValueChange={onValueChange}
      disabled={disabled}
      disableHeader
      placeholder={placeholder ?? undefined}
      identifierField="code"
      enabled={enabled}
    />
  );
};

interface EmCostCodeDropdownProps {
  selectedEmCostCode?: AgaveEmCostCode | null;
  /** EMGroup of the selected equipment — scopes the list. */
  emGroup?: string | null;
  /** SEE-1435: EMCo + department of the selected equipment, for department scoping. */
  emCo?: string | null;
  department?: string | null;
  onValueChange: (option: AgaveEmCostCode | null) => void;
  disabled?: boolean;
  enabled?: boolean;
  placeholder?: string | null;
}

export const AgaveEmCostCodeDropdown = ({
  selectedEmCostCode,
  emGroup,
  emCo,
  department,
  onValueChange,
  disabled,
  enabled = true,
  placeholder,
}: EmCostCodeDropdownProps): JSX.Element => {
  // SEE-1325: server-side search, like every other coding entity. The EMGroup /
  // department scoping that used to run in a useMemo here now runs in
  // `search_agave_em_cost_codes`, so this is a thin wrapper like the equipment
  // dropdown above.
  return (
    <CodingEntityDropdown<AgaveEmCostCode>
      entityType="emCostCodes"
      label="Equipment Cost Code"
      selectedItem={selectedEmCostCode}
      onValueChange={onValueChange}
      disabled={disabled}
      disableHeader
      placeholder={placeholder ?? undefined}
      identifierField="code"
      emGroup={emGroup ?? undefined}
      emCo={emCo ?? undefined}
      department={department ?? undefined}
      enabled={enabled}
      dataDropdownType="agave-em-cost-code"
      fetchOnlyOnOpen
    />
  );
};

interface EmCostTypeDropdownProps {
  selectedEmCostType?: AgaveEmCostType | null;
  /** EMGroup of the selected equipment — scopes the list. */
  emGroup?: string | null;
  /**
   * SEE-975/SEE-1325: id of the selected EM cost code. The cost-type list is
   * scoped to its valid Vista pairs server-side, via the
   * AgaveEmCostCodeCostType join in `search_agave_em_cost_types`. (The
   * client-side `emCostTypeCodes` scoping this doc used to describe now only
   * backs the auto-clear in handleChangeEmCostCode.)
   */
  selectedEmCostCodeId?: string | null;
  /** SEE-1435: EMCo + department of the selected equipment, for department scoping. */
  emCo?: string | null;
  department?: string | null;
  onValueChange: (option: AgaveEmCostType | null) => void;
  disabled?: boolean;
  enabled?: boolean;
  placeholder?: string | null;
}

export const AgaveEmCostTypeDropdown = ({
  selectedEmCostType,
  emGroup,
  emCo,
  department,
  selectedEmCostCodeId,
  onValueChange,
  disabled,
  enabled = true,
  placeholder,
}: EmCostTypeDropdownProps): JSX.Element => {
  // SEE-1325: both constraints now run server-side in
  // `search_agave_em_cost_types` — the cost-code/cost-type pair (via the
  // AgaveEmCostCodeCostType join, which is what Vista rejects the posting on)
  // intersected with the department's Cost Types tab.
  return (
    <CodingEntityDropdown<AgaveEmCostType>
      entityType="emCostTypes"
      label="Equipment Cost Type"
      selectedItem={selectedEmCostType}
      onValueChange={onValueChange}
      disabled={disabled}
      disableHeader
      placeholder={placeholder ?? undefined}
      identifierField="code"
      emGroup={emGroup ?? undefined}
      emCo={emCo ?? undefined}
      department={department ?? undefined}
      emCostCodeId={selectedEmCostCodeId ?? undefined}
      enabled={enabled}
      dataDropdownType="agave-em-cost-type"
      fetchOnlyOnOpen
    />
  );
};
